import * as _ from 'lodash';
import moment from 'moment';
import PropTypes from 'prop-types';
import qs from 'qs';
import { Component } from 'react';
import { Button } from 'react-bootstrap';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import type {
  PageChangeFunction,
  PageSizeChangeFunction,
  SortedChangeFunction,
} from 'react-table';

import AuditLogTable from './AuditLogTable';
import AuditLogModule from '../../../redux/audit_logs';
import AccessGate from '../../generic/Gates/AccessGate';
import { Select } from '../../generic/Select';
import Icon, { icons } from '../../generic/Icon';
import ModalDateRangePicker from '../../modals/ModalDateRangePicker';
import Header from '../../shared/settings/SettingsPageHeader';
import type { LoadAuditLogsParams } from '../../../redux/audit_logs/actions';
import type { UserSelectOption, TopicActionSelectOption } from '../../../redux/audit_logs/store';
import type { Build, AuditLog, RouterProps } from '../../../types';
import styles from './AuditLogs.module.less';
import { isTrue } from '../../../utils';

const usedQueryParams = ['page', 'pageSize', 'startDate', 'endDate', 'topics', 'actions', 'actingUserIds', 'topicActions', 'sortBy', 'sortDir'];

type AuditLogsPageProps = RouterProps & {
  status: {
    loading?: boolean;
    errors?: string[];
  },
  logs: AuditLog[];
  logCount: number;
  userOptions?: UserSelectOption[];
  topicActionOptions?: TopicActionSelectOption[];
  loadAuditLogs: (query: LoadAuditLogsParams) => void;
  loadAuditLogsCount: (query: LoadAuditLogsParams) => void;
  loadAuditLogTopicActionOptions: () => void;
  loadAuditLogUserFilterOptions: () => void;
};

type AuditLogsPageQueryState = {
  page: number;
  pageSize: number;
  startDate?: object;
  endDate?: object;
  topics?: string[];
  actingUserIds?: number[];
  actions?: string[];
  topicActions?: string[];
  sortBy?: string;
  sortDir?: string;
};

type AuditLogsPageState = {
  query: AuditLogsPageQueryState;
  loading?: boolean;
  builds?: Build[];
  datePickerOpen?: boolean;
  rebuilding?: boolean;
};

const defaultQuery = {
  page: 0,
  pageSize: 10,
  topics: [],
  actions: [],
  actingUserIds: [],
  topicActions: [],
};

const dateFormat = 'YYYY-MM-DD';

class AuditLogsPage extends Component<AuditLogsPageProps, AuditLogsPageState> {
  constructor(props: AuditLogsPageProps) {
    super(props);
    const queryWithDefaults = {
      startDate: moment().startOf('day').subtract(1, 'month'),
      endDate: moment().endOf('day'),
      ...defaultQuery,
      ...props.location.query,
    };
    this.state = {
      query: _.pick(queryWithDefaults, usedQueryParams),
      datePickerOpen: false,
    };
  }

  componentDidMount() {
    this.updateStateFromLocation();
    this.loadOptions();
    this.loadAuditLogs();
  }

  componentDidUpdate(prevProps: Readonly<AuditLogsPageProps>, prevState: Readonly<AuditLogsPageState>) {
    if (!_.isEqual(this.props.location, prevProps.location)) {
      this.updateStateFromLocation();
    }
    if (!_.isEqual(this.state.query, prevState.query)) {
      this.updateLocationFromState();
      this.loadAuditLogs();
    }
  }

  get auditLogHeading(): 'All' | 'User' | 'Issue' {
    const { pathname } = this.props.location;
    const lastPathSegment = pathname.split('/').pop();
    if (lastPathSegment === 'users') {
      return 'User';
    }
    if (lastPathSegment === 'issues') {
      return 'Issue';
    }
    return 'All';
  }

  get routeQueryParams() {
    const {
      startDate, endDate, topics, actions, topicActions, actingUserIds, page, pageSize, sortBy, sortDir,
    } = this.state.query;
    const startDateString = startDate ? moment(startDate).format(dateFormat) : null;
    const endDateString = endDate ? moment(endDate).format(dateFormat) : null;

    return {
      ...(_.omitBy({
        startDate: startDateString,
        endDate: endDateString,
        topics,
        actions,
        actingUserIds,
        topicActions,
        page,
        pageSize,
        sortBy,
        sortDir,
      }, _.isEmpty)),
      // these get filtered out by omitBy, so we need to add them here
      page,
      pageSize,
    };
  }

  get sidebarPathPart() {
    const pathParts = this.props.location.pathname.split('/');
    const auditLogsPartIndex = pathParts.indexOf('audit-logs');
    if (auditLogsPartIndex > 0) {
      return pathParts[auditLogsPartIndex + 1];
    }
    return null;
  }

  loadOptions() {
    this.props.loadAuditLogTopicActionOptions();
    this.props.loadAuditLogUserFilterOptions();
  }

  searchFromState(state: AuditLogsPageState) {
    const searchParams = _.omitBy(state.query,
      (value, key) => key.endsWith('Date') ? value === null : _.isEmpty(value));
    const filteredParams = _.pick(searchParams, usedQueryParams);
    if (filteredParams.startDate) {
      filteredParams.startDate = moment(filteredParams.startDate).format('YYYY-MM-DD');
    }
    if (filteredParams.endDate) {
      filteredParams.endDate = moment(filteredParams.endDate).format('YYYY-MM-DD');
    }
    if (_.isEqual(filteredParams.topics, this.pathPartToTopics(this.sidebarPathPart))) {
      delete filteredParams.topics;
    }

    return qs.stringify(filteredParams, { arrayFormat: 'comma' });
  }

  stateQueryFromSearch(search: string): Partial<AuditLogsPageQueryState> {
    const sidebarPathPart = this.sidebarPathPart;
    const searchString = search.startsWith('?') ? search.substr(1) : search;
    const searchParams = _.omitBy(qs.parse(searchString, { comma: true }), _.isEmpty);
    const filteredParams = _.pick(searchParams, usedQueryParams);
    // Convert qs parsed parameters to state types:
    ['startDate', 'endDate'].forEach(dateParam => {
      if (filteredParams[dateParam]) {
        filteredParams[dateParam] = moment(filteredParams[dateParam]);
      }
    });
    ['actingUserIds', 'topicActions', 'topics', 'actions'].forEach(arrayParam => {
      const paramValue = filteredParams[arrayParam];
      if (paramValue && _.isString(paramValue)) {
        filteredParams[arrayParam] = paramValue.split(',');
      }
    });
    if (filteredParams.actingUserIds) {
      filteredParams.actingUserIds = filteredParams.actingUserIds.map(x => Number.parseInt(x, 10));
    }

    return {
      topics: this.pathPartToTopics(sidebarPathPart),
      ...filteredParams,
    };
  }

  updateLocationFromState() {
    const newLocation = {
      pathname: this.props.location.pathname,
      search: `?${this.searchFromState(this.state)}`,
    };
    this.context.router.push(newLocation);
  }

  updateStateFromLocation() {
    this.setState((state: AuditLogsPageState, props: AuditLogsPageProps) => ({
      query: {
        ...state.query,
        ...this.stateQueryFromSearch(props.location.search),
      },
    }));
  }

  loadAuditLogs() {
    const newQueryParams = this.routeQueryParams;
    const apiQueryParams = _.omit(newQueryParams, 'page', 'pageSize');
    if (Number.isInteger(newQueryParams.page)) {
      if (Number.isInteger(newQueryParams.pageSize)) {
        apiQueryParams.limit = newQueryParams.pageSize;
      }
      apiQueryParams.offset = newQueryParams.page * newQueryParams.pageSize;
    }

    return Promise.all([
      this.props.loadAuditLogs(apiQueryParams),
      this.props.loadAuditLogsCount(apiQueryParams),
    ]);
  }

  pathPartToTopics(urlSegment: string) {
    switch (urlSegment) {
      case 'users':
        return ['User', 'Team'];
      case 'issues':
        return ['Issue'];
      default:
        return [];
    }
  }

  handleChangeActingUsers = actingUsers => {
    this.setState((state: AuditLogsPageState) => ({
      query: {
        ...state.query,
        actingUserIds: (actingUsers || []).map((user) => user.value),
        page: 0,
      },
    }));
  };

  handleChangeTopicActions = topicActions => {
    this.setState((state: AuditLogsPageState) => ({
      query: {
        ...state.query,
        topicActions: (topicActions || []).map((topicAction) => topicAction.value),
        page: 0,
      },
    }));
  };

  handleChangeDateRange = (range: { startDate: Date; endDate: Date }) => {
    this.setState((state: AuditLogsPageState) => ({
      query: {
        ...state.query,
        startDate: moment(range.startDate) || '',
        endDate: moment(range.endDate) || '',
        page: 0,
      },
    }));
  };

  handleChangeTablePage: PageChangeFunction = (pageIndex) => {
    this.setState((state: AuditLogsPageState) => ({
      query: {
        ...state.query,
        page: pageIndex,
      },
    }));
  };

  handleChangeTablePageSize: PageSizeChangeFunction = (pageSize, pageIndex) => {
    this.setState((state: AuditLogsPageState) => ({
      query: {
        ...state.query,
        pageSize,
        page: pageIndex,
      },
    }));
  };

  handleChangeTableSort: SortedChangeFunction = (newSorted, column) => {
    this.setState((state: AuditLogsPageState) => ({
      query: {
        ...state.query,
        sortBy: column.id,
        sortDir: isTrue(newSorted[0]?.desc) ? 'DESC' : 'ASC',
      },
    }));
  };

  render() {
    const { query } = this.state;
    const { userOptions, topicActionOptions, logs } = this.props;
    // Date Picker config
    const startDate = moment(query.startDate);
    const startLabel = startDate.format('MMMM Do');
    const endDate = moment(query.endDate);
    const endLabel = endDate.format('MMMM Do');
    const dateLabel = startLabel === endLabel ? startLabel : `${startLabel} - ${endLabel}`;

    let visibleTopicActionOptions = topicActionOptions;
    if (query.topics.length > 0) {
      visibleTopicActionOptions = topicActionOptions.filter(option => query.topics.includes(option.topic));
    }
    const selectedTopicActionOptions = topicActionOptions?.filter(option => query.topicActions?.includes(option.value));

    const groupedOptions = Object.entries(_.groupBy(visibleTopicActionOptions ?? {}, ({ topic }) => topic)).map(
      ([topic, options]) => ({
        label: _.words(topic).join(' '),
        options,
      }),
    );

    return (
      <>
        <div className={styles.header}>
          <Header heading={`${this.auditLogHeading} Logs`} />
        </div>
        <AccessGate level="premium" className="padding-xl">
          <div className="row">
            <div className="col-xs-12">
              <div className={styles.actionsBar}>
                <div className={styles.actionsBarChild}>
                  <Select
                    isClearable
                    isMulti
                    id="user-filter"
                    placeholder="Select Users by email..."
                    menuPortalTarget={document.body}
                    options={userOptions}
                    onChange={this.handleChangeActingUsers}
                    value={userOptions?.filter(option => query.actingUserIds?.includes(_.toNumber(option.value)))}
                  />
                </div>
                <div className={styles.actionsBarChild}>
                  <Select
                    isClearable
                    isMulti
                    id="action-filter"
                    placeholder="Select Actions..."
                    menuPortalTarget={document.body}
                    options={groupedOptions}
                    onChange={this.handleChangeTopicActions}
                    value={selectedTopicActionOptions}
                  />
                </div>
                <div className={styles.actionsBarChild}>
                  <Button onClick={() => this.setState({ datePickerOpen: true })}>
                    {dateLabel} <Icon icon={icons.chevronDown} />
                  </Button>
                </div>
              </div>
              <ModalDateRangePicker
                show={this.state.datePickerOpen}
                onSetDateRange={this.handleChangeDateRange}
                startDate={startDate.format(dateFormat)}
                endDate={endDate.format(dateFormat)}
                onHide={() => this.setState({ datePickerOpen: false })}
              />
            </div>
          </div>
          <AuditLogTable
            pageSize={query?.pageSize || 10}
            page={query?.page || 0}
            isLoading={this.props.status.loading || false}
            onPageChange={this.handleChangeTablePage}
            onPageSizeChange={this.handleChangeTablePageSize}
            onSortedChange={this.handleChangeTableSort}
            logs={logs}
            logCount={this.props.logCount}
          />
        </AccessGate>
      </>
    );
  }
}

AuditLogsPage.contextTypes = {
  // eslint-disable-next-line react/forbid-prop-types -- We'll fix this later.
  router: PropTypes.object.isRequired,
};

const mapStateToProps = createStructuredSelector({
  status: AuditLogModule.selectors.getStatus,
  logCount: AuditLogModule.selectors.getCount,
  logs: AuditLogModule.selectors.getLogs,
  topicActionOptions: AuditLogModule.selectors.getTopicActionOptions,
  userOptions: AuditLogModule.selectors.getUserOptions,
});

const mapDispatchToProps = {
  loadAuditLogsCount: AuditLogModule.actions.loadAuditLogCount,
  loadAuditLogs: AuditLogModule.actions.loadAuditLogs,
  loadAuditLogTopicActionOptions: AuditLogModule.actions.loadAuditLogTopicActionOptions,
  loadAuditLogUserFilterOptions: AuditLogModule.actions.loadAuditLogUserFilterOptions,
};

export default connect(mapStateToProps, mapDispatchToProps)(AuditLogsPage);
